import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp,
  TrendingDown,
  Building2,
  Calendar,
  Banknote,
  CreditCard,
  Home,
  ShoppingCart,
  Car,
  Briefcase,
  AlertTriangle,
  Search,
  X,
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  BarChart3,
  PieChart,
  List,
  CheckCircle2,
  XCircle,
  UserCheck
} from "lucide-react";

// Category icons mapping
const CATEGORY_ICONS = {
  "Transfers": Banknote,
  "Payroll": Briefcase,
  "Rent": Home,
  "Utilities": Home,
  "Shopping": ShoppingCart,
  "Food": ShoppingCart,
  "Transport": Car,
  "Travel": Car,
  "Financial Services": CreditCard,
  "Loans": CreditCard,
  "Client Payments": Banknote,
  "Default": DollarSign
};

// Category colors
const CATEGORY_COLORS = {
  "Transfers": "#3B82F6",
  "Payroll": "#10B981",
  "Rent": "#F59E0B",
  "Utilities": "#F97316",
  "Shopping": "#8B5CF6",
  "Food": "#EC4899",
  "Transport": "#06B6D4",
  "Travel": "#14B8A6",
  "Financial Services": "#EF4444",
  "Loans": "#DC2626",
  "Client Payments": "#22C55E",
  "Default": "#6B7280"
};

// Fuzzy string matching using Levenshtein distance
const levenshteinDistance = (str1, str2) => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 0;
  if (s1.length === 0) return s2.length;
  if (s2.length === 0) return s1.length;
  
  const matrix = [];
  for (let i = 0; i <= s2.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= s1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      const cost = s1[j - 1] === s2[i - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  
  return matrix[s2.length][s1.length];
};

// Calculate name match score (0-100)
const calculateNameMatchScore = (name1, name2) => {
  if (!name1 || !name2) return { score: 0, status: 'unknown' };
  
  // Normalize names - remove common business suffixes
  const normalize = (name) => {
    return name
      .toLowerCase()
      .replace(/\b(ltd|limited|plc|inc|llc|corp|corporation|company|co)\b/gi, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  const n1 = normalize(name1);
  const n2 = normalize(name2);
  
  // Exact match after normalization
  if (n1 === n2) return { score: 100, status: 'match' };
  
  // Check if one contains the other
  if (n1.includes(n2) || n2.includes(n1)) {
    return { score: 90, status: 'match' };
  }
  
  // Check word overlap
  const words1 = n1.split(' ').filter(w => w.length > 2);
  const words2 = n2.split(' ').filter(w => w.length > 2);
  const commonWords = words1.filter(w => words2.includes(w));
  const wordOverlapScore = (commonWords.length / Math.max(words1.length, words2.length)) * 100;
  
  if (wordOverlapScore >= 50) {
    return { score: Math.round(wordOverlapScore), status: wordOverlapScore >= 70 ? 'match' : 'partial' };
  }
  
  // Levenshtein distance-based similarity
  const maxLen = Math.max(n1.length, n2.length);
  const distance = levenshteinDistance(n1, n2);
  const similarity = ((maxLen - distance) / maxLen) * 100;
  
  if (similarity >= 80) return { score: Math.round(similarity), status: 'match' };
  if (similarity >= 60) return { score: Math.round(similarity), status: 'partial' };
  return { score: Math.round(similarity), status: 'mismatch' };
};

export default function BankStatementViewer({ open, onClose, bankData, applicationInfo }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Parse and structure the data
  const parsedData = useMemo(() => {
    if (!bankData) return null;

    // Use transactions extracted by GPT from backend (preferred)
    // Fall back to client-side parsing only if backend didn't provide transactions
    let transactions = bankData.transactions || [];
    
    // If no backend transactions, try to parse from OCR text (fallback)
    if (transactions.length === 0) {
      const recognized = bankData.recognized_text || "";
      
      // Pattern: DD/MM/YY Description [Credit|Debit] Amount Balance Amount
      const transactionRegex = /(\d{2}\/\d{2}\/\d{2,4})\s+(.+?)\s+(Credit|Debit)\s+([\d,]+\.?\d*)\s+Balance\s+([\d,]+\.?\d*)/gi;
      
      let match;
      while ((match = transactionRegex.exec(recognized)) !== null) {
        const [, date, description, type, amountStr, balanceStr] = match;
        const isCredit = type.toLowerCase() === 'credit';
        const amount = parseFloat(amountStr?.replace(/,/g, '') || 0);
        const balance = parseFloat(balanceStr?.replace(/,/g, '') || 0);
        
        // Categorize the transaction
        let category = "Other";
        const desc = description?.toLowerCase() || "";
        if (desc.includes('rent')) category = "Rent";
        else if (desc.includes('payroll') || desc.includes('salary')) category = "Payroll";
        else if (desc.includes('client') || desc.includes('payment') || desc.includes('invoice')) category = "Client Payments";
        else if (desc.includes('loan') || desc.includes('finance')) category = "Financial Services";
        
        transactions.push({
          date: date,
          description: description?.trim(),
          type: isCredit ? 'credit' : 'debit',
          amount: amount,
          balance: balance,
          category: category
        });
      }
    }

    // Calculate monthly breakdown from bankData
    const monthlyBreakdown = bankData.monthly_breakdown || [];
    
    // Calculate category totals from transactions
    const categoryTotals = {
      incoming: {},
      outgoing: {}
    };
    
    transactions.forEach(t => {
      const target = t.type === 'credit' ? categoryTotals.incoming : categoryTotals.outgoing;
      if (!target[t.category]) target[t.category] = 0;
      target[t.category] += t.amount;
    });

    return {
      transactions,
      monthlyBreakdown,
      categoryTotals,
      totalCredits: bankData.financial_summary?.total_credits || bankData.average_monthly_credits * 3 || 0,
      totalDebits: bankData.financial_summary?.total_debits || bankData.average_monthly_debits * 3 || 0,
      avgMonthlyIn: bankData.average_monthly_credits || 0,
      avgMonthlyOut: bankData.average_monthly_debits || 0,
      openingBalance: bankData.financial_summary?.opening_balance,
      closingBalance: bankData.financial_summary?.closing_balance,
      incomeIndicators: bankData.income_indicators || {},
      expenseIndicators: bankData.expense_indicators || {},
      riskFactors: bankData.risk_factors || {}
    };
  }, [bankData]);

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    if (!parsedData?.transactions) return [];
    
    return parsedData.transactions.filter(t => {
      const matchesSearch = !searchTerm || 
        t.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === "all" || t.category === categoryFilter;
      const matchesType = typeFilter === "all" || t.type === typeFilter;
      return matchesSearch && matchesCategory && matchesType;
    });
  }, [parsedData, searchTerm, categoryFilter, typeFilter]);

  // Get unique categories
  const categories = useMemo(() => {
    if (!parsedData?.transactions) return [];
    return [...new Set(parsedData.transactions.map(t => t.category))];
  }, [parsedData]);

  // Calculate name match score between applicant and bank statement account holder
  const nameMatch = useMemo(() => {
    const applicantName = applicationInfo?.company_name;
    const accountHolder = bankData?.account_holder;
    return calculateNameMatchScore(applicantName, accountHolder);
  }, [applicationInfo?.company_name, bankData?.account_holder]);

  if (!bankData) return null;

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return "N/A";
    return `£${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getCategoryIcon = (category) => {
    const Icon = CATEGORY_ICONS[category] || CATEGORY_ICONS.Default;
    return Icon;
  };

  const getCategoryColor = (category) => {
    return CATEGORY_COLORS[category] || CATEGORY_COLORS.Default;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] max-h-[90vh] p-0 overflow-hidden">
        {/* Header */}
        <div className="bg-slate-800 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Building2 className="w-8 h-8" />
              <div>
                <h2 className="text-lg font-bold">{applicationInfo?.company_name || "Bank Statement Analysis"}</h2>
                <p className="text-sm text-slate-300">
                  Reference: {applicationInfo?.application_id || bankData.filename}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              {/* Name Match Indicator */}
              <div className={`p-2 rounded-lg border ${
                nameMatch.status === 'match' ? 'bg-green-900/50 border-green-600' :
                nameMatch.status === 'partial' ? 'bg-amber-900/50 border-amber-600' :
                nameMatch.status === 'mismatch' ? 'bg-red-900/50 border-red-600' :
                'bg-slate-700 border-slate-600'
              }`}>
                <div className="flex items-center gap-2 text-sm">
                  {nameMatch.status === 'match' ? (
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  ) : nameMatch.status === 'partial' ? (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  ) : nameMatch.status === 'mismatch' ? (
                    <XCircle className="w-4 h-4 text-red-400" />
                  ) : (
                    <UserCheck className="w-4 h-4 text-slate-400" />
                  )}
                  <span className={`font-medium ${
                    nameMatch.status === 'match' ? 'text-green-400' :
                    nameMatch.status === 'partial' ? 'text-amber-400' :
                    nameMatch.status === 'mismatch' ? 'text-red-400' :
                    'text-slate-400'
                  }`}>
                    Name Match: {nameMatch.score}%
                  </span>
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  BS: {bankData.account_holder || "Unknown"}
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {bankData.statement_period?.start_date || "N/A"} to {bankData.statement_period?.end_date || "N/A"}
                  </span>
                </div>
                <div className="text-sm font-medium mt-1">
                  {bankData.bank_name || "Unknown Bank"} • {bankData.account_holder || ""}
                </div>
              </div>
              <Badge className="bg-green-600 text-white px-3 py-1">
                Snapshot {new Date(bankData.timestamp).toLocaleDateString("en-GB")}
              </Badge>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <div className="flex gap-2 mt-4">
            <Button 
              variant={activeTab === "overview" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("overview")}
              className={activeTab !== "overview" ? "text-slate-300 hover:text-white" : ""}
            >
              <BarChart3 className="w-4 h-4 mr-1" /> Overview
            </Button>
            <Button 
              variant={activeTab === "transactions" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("transactions")}
              className={activeTab !== "transactions" ? "text-slate-300 hover:text-white" : ""}
            >
              <List className="w-4 h-4 mr-1" /> Transactions
            </Button>
            <Button 
              variant={activeTab === "analysis" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("analysis")}
              className={activeTab !== "analysis" ? "text-slate-300 hover:text-white" : ""}
            >
              <PieChart className="w-4 h-4 mr-1" /> Analysis
            </Button>
          </div>
        </div>

        <ScrollArea className="h-[calc(90vh-140px)]">
          <div className="p-4">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-4">
                {/* Key Metrics Row */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700 mb-1">
                      <ArrowUpRight className="w-5 h-5" />
                      <span className="text-sm font-medium">Total In</span>
                    </div>
                    <div className="text-2xl font-bold text-green-700">
                      {formatCurrency(parsedData?.totalCredits)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Avg: {formatCurrency(parsedData?.avgMonthlyIn)}/month
                    </div>
                  </div>
                  
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700 mb-1">
                      <ArrowDownRight className="w-5 h-5" />
                      <span className="text-sm font-medium">Total Out</span>
                    </div>
                    <div className="text-2xl font-bold text-red-700">
                      {formatCurrency(parsedData?.totalDebits)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Avg: {formatCurrency(parsedData?.avgMonthlyOut)}/month
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-blue-700 mb-1">
                      <TrendingUp className="w-5 h-5" />
                      <span className="text-sm font-medium">Net Flow</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-700">
                      {formatCurrency((parsedData?.totalCredits || 0) - (parsedData?.totalDebits || 0))}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Over statement period
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-700 mb-1">
                      <Banknote className="w-5 h-5" />
                      <span className="text-sm font-medium">Closing Balance</span>
                    </div>
                    <div className="text-2xl font-bold text-slate-700">
                      {formatCurrency(parsedData?.closingBalance)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {parsedData?.openingBalance ? `Opening: ${formatCurrency(parsedData.openingBalance)}` : ""}
                    </div>
                  </div>
                </div>

                {/* Monthly Breakdown */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Incoming Transactions by Month */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      Incoming Transactions
                    </h3>
                    <div className="space-y-3">
                      {parsedData?.monthlyBreakdown?.map((month, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-16 text-sm text-muted-foreground">{month.month}</div>
                          <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                            <div 
                              className="bg-green-500 h-full rounded-full flex items-center justify-end pr-2"
                              style={{ width: `${Math.min(100, (month.credits / (parsedData?.avgMonthlyIn * 1.5 || 1)) * 100)}%` }}
                            >
                              <span className="text-xs text-white font-medium">
                                {formatCurrency(month.credits)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!parsedData?.monthlyBreakdown || parsedData.monthlyBreakdown.length === 0) && (
                        <div className="text-sm text-muted-foreground text-center py-4">
                          No monthly breakdown available
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Outgoing Transactions by Month */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                      Outgoing Transactions
                    </h3>
                    <div className="space-y-3">
                      {parsedData?.monthlyBreakdown?.map((month, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-16 text-sm text-muted-foreground">{month.month}</div>
                          <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                            <div 
                              className="bg-red-500 h-full rounded-full flex items-center justify-end pr-2"
                              style={{ width: `${Math.min(100, (month.debits / (parsedData?.avgMonthlyOut * 1.5 || 1)) * 100)}%` }}
                            >
                              <span className="text-xs text-white font-medium">
                                {formatCurrency(month.debits)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!parsedData?.monthlyBreakdown || parsedData.monthlyBreakdown.length === 0) && (
                        <div className="text-sm text-muted-foreground text-center py-4">
                          No monthly breakdown available
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Category Breakdown Row */}
                <div className="grid grid-cols-3 gap-4">
                  {/* Financial Services */}
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Financial Services
                    </h3>
                    <div className="space-y-2">
                      {bankData.expense_indicators?.loan_repayments_detected && (
                        <div className="flex justify-between text-sm">
                          <span>Loan Repayments</span>
                          <span className="text-red-600 font-medium">
                            -{formatCurrency(bankData.expense_indicators.loan_repayment_total)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm border-t pt-2 mt-2">
                        <span className="font-medium">Monthly Avg</span>
                        <span className="text-red-600 font-medium">
                          -{formatCurrency((bankData.expense_indicators?.loan_repayment_total || 0) / 3)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Income Analysis */}
                  <div className="border rounded-lg p-4 bg-green-50">
                    <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Income Analysis
                    </h3>
                    <div className="space-y-2">
                      {bankData.income_indicators?.income_sources?.slice(0, 3).map((source, idx) => (
                        <div key={idx} className="text-sm text-green-700">
                          • {source}
                        </div>
                      ))}
                      <div className="flex justify-between text-sm border-t border-green-200 pt-2 mt-2">
                        <span className="font-medium">Monthly Avg</span>
                        <span className="text-green-700 font-medium">
                          +{formatCurrency(bankData.income_indicators?.estimated_monthly_income || parsedData?.avgMonthlyIn)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Regular Outgoings */}
                  <div className="border rounded-lg p-4 bg-amber-50">
                    <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                      <Home className="w-4 h-4" />
                      Regular Outgoings
                    </h3>
                    <div className="space-y-2">
                      {bankData.expense_indicators?.payroll_detected && (
                        <div className="flex justify-between text-sm">
                          <span>Payroll</span>
                          <span className="text-red-600">
                            -{formatCurrency(bankData.expense_indicators.estimated_monthly_payroll)}/mo
                          </span>
                        </div>
                      )}
                      {bankData.expense_indicators?.rent_detected && (
                        <div className="flex justify-between text-sm">
                          <span>Rent</span>
                          <span className="text-red-600">
                            -{formatCurrency(bankData.expense_indicators.rent_amount)}/mo
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm border-t border-amber-200 pt-2 mt-2">
                        <span className="font-medium">Monthly Avg</span>
                        <span className="text-red-600 font-medium">
                          -{formatCurrency(parsedData?.avgMonthlyOut)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Factors */}
                {bankData.risk_factors && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      Risk Indicators
                    </h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className={`p-3 rounded-lg ${bankData.risk_factors.bounced_payments > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} border`}>
                        <div className="text-2xl font-bold text-center">
                          {bankData.risk_factors.bounced_payments || 0}
                        </div>
                        <div className="text-xs text-center text-muted-foreground">Bounced Payments</div>
                      </div>
                      <div className={`p-3 rounded-lg ${bankData.risk_factors.overdraft_usage ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'} border`}>
                        <div className="text-2xl font-bold text-center">
                          {bankData.risk_factors.overdraft_usage ? "Yes" : "No"}
                        </div>
                        <div className="text-xs text-center text-muted-foreground">Overdraft Usage</div>
                      </div>
                      <div className={`p-3 rounded-lg ${bankData.risk_factors.gambling_transactions ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} border`}>
                        <div className="text-2xl font-bold text-center">
                          {bankData.risk_factors.gambling_transactions ? "Yes" : "No"}
                        </div>
                        <div className="text-xs text-center text-muted-foreground">Gambling</div>
                      </div>
                      <div className={`p-3 rounded-lg ${bankData.risk_factors.high_cash_withdrawals ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'} border`}>
                        <div className="text-2xl font-bold text-center">
                          {bankData.risk_factors.high_cash_withdrawals ? "Yes" : "No"}
                        </div>
                        <div className="text-xs text-center text-muted-foreground">High Cash Withdrawals</div>
                      </div>
                    </div>
                    {bankData.risk_factors.unusual_patterns?.length > 0 && (
                      <div className="mt-3 p-2 bg-amber-50 rounded text-sm text-amber-700">
                        <span className="font-medium">Unusual Patterns: </span>
                        {bankData.risk_factors.unusual_patterns.join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === "transactions" && (
              <div className="space-y-4">
                {/* Filters */}
                <div className="flex items-center gap-4 p-4 bg-slate-100 rounded-lg">
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search transactions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="credit">Credits Only</SelectItem>
                      <SelectItem value="debit">Debits Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Transaction Summary */}
                <div className="flex items-center justify-between p-3 bg-slate-800 text-white rounded-lg">
                  <span className="font-medium">Transactions ({filteredTransactions.length})</span>
                  <div className="flex items-center gap-6">
                    <div>
                      <span className="text-slate-300 text-sm">total in: </span>
                      <span className="text-green-400 font-medium">
                        +{formatCurrency(filteredTransactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.amount, 0))}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-300 text-sm">total out: </span>
                      <span className="text-red-400 font-medium">
                        -{formatCurrency(filteredTransactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.amount, 0))}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-300 text-sm">sum total: </span>
                      <span className={`font-medium ${
                        filteredTransactions.reduce((sum, t) => sum + (t.type === 'credit' ? t.amount : -t.amount), 0) >= 0 
                          ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {formatCurrency(Math.abs(filteredTransactions.reduce((sum, t) => sum + (t.type === 'credit' ? t.amount : -t.amount), 0)))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Transaction List */}
                <div className="border rounded-lg divide-y max-h-[500px] overflow-y-auto">
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((t, idx) => {
                      const Icon = getCategoryIcon(t.category);
                      return (
                        <div key={idx} className="p-3 hover:bg-slate-50 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: `${getCategoryColor(t.category)}20` }}
                            >
                              <Icon className="w-5 h-5" style={{ color: getCategoryColor(t.category) }} />
                            </div>
                            <div>
                              <div className="font-medium text-sm">{t.description}</div>
                              <div className="text-xs text-muted-foreground">
                                {t.category} • {t.date}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {t.type === 'credit' ? '+' : '-'}{formatCurrency(t.amount)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Balance: {formatCurrency(t.balance)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No transactions found matching your filters</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Analysis Tab */}
            {activeTab === "analysis" && (
              <div className="space-y-4">
                {/* Affordability Summary */}
                <div className={`p-6 rounded-lg border-2 ${
                  bankData.affordability_indicators?.recommendation === 'APPROVED' ? 'border-green-500 bg-green-50' :
                  bankData.affordability_indicators?.recommendation === 'REVIEW' ? 'border-amber-500 bg-amber-50' :
                  'border-red-500 bg-red-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold">AI Affordability Analysis</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Based on {bankData.statement_period?.months_covered || 3} months of bank statement data
                      </p>
                    </div>
                    <Badge className={`text-lg px-4 py-2 ${
                      bankData.affordability_indicators?.recommendation === 'APPROVED' ? 'bg-green-600' :
                      bankData.affordability_indicators?.recommendation === 'REVIEW' ? 'bg-amber-600' :
                      'bg-red-600'
                    } text-white`}>
                      {bankData.affordability_indicators?.recommendation || 'PENDING'}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 mt-6">
                    <div className="bg-white rounded-lg p-4 border">
                      <div className="text-sm text-muted-foreground">Debt-to-Income Ratio</div>
                      <div className="text-2xl font-bold mt-1">
                        {((bankData.affordability_indicators?.debt_to_income_ratio || 0) * 100).toFixed(1)}%
                      </div>
                      <div className={`text-xs mt-1 ${
                        (bankData.affordability_indicators?.debt_to_income_ratio || 0) <= 0.35 ? 'text-green-600' :
                        (bankData.affordability_indicators?.debt_to_income_ratio || 0) <= 0.43 ? 'text-amber-600' :
                        'text-red-600'
                      }`}>
                        {(bankData.affordability_indicators?.debt_to_income_ratio || 0) <= 0.35 ? 'Healthy' :
                         (bankData.affordability_indicators?.debt_to_income_ratio || 0) <= 0.43 ? 'Moderate' : 'High'}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border">
                      <div className="text-sm text-muted-foreground">Disposable Income</div>
                      <div className="text-2xl font-bold mt-1 text-green-600">
                        {formatCurrency(bankData.affordability_indicators?.disposable_income)}
                      </div>
                      <div className="text-xs mt-1 text-muted-foreground">Per month</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border">
                      <div className="text-sm text-muted-foreground">Stability Score</div>
                      <div className="text-2xl font-bold mt-1">
                        {bankData.affordability_indicators?.stability_score || 'N/A'}
                      </div>
                      <div className={`text-xs mt-1 ${
                        bankData.affordability_indicators?.stability_score === 'HIGH' ? 'text-green-600' :
                        bankData.affordability_indicators?.stability_score === 'MEDIUM' ? 'text-amber-600' :
                        'text-red-600'
                      }`}>
                        Income consistency
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border">
                      <div className="text-sm text-muted-foreground">Meets £5k Min Sales</div>
                      <div className={`text-2xl font-bold mt-1 ${bankData.meets_min_sales ? 'text-green-600' : 'text-red-600'}`}>
                        {bankData.meets_min_sales ? 'Yes' : 'No'}
                      </div>
                      <div className="text-xs mt-1 text-muted-foreground">
                        Avg: {formatCurrency(bankData.average_monthly_credits)}/mo
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Notes */}
                {bankData.notes && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">AI Analysis Notes</h4>
                    <p className="text-sm text-blue-700">{bankData.notes}</p>
                  </div>
                )}

                {/* OCR Preview */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">OCR Extracted Text Preview</h4>
                  <pre className="text-xs bg-slate-50 p-3 rounded overflow-x-auto max-h-[200px] overflow-y-auto whitespace-pre-wrap">
                    {bankData.recognized_text || bankData.ocr_text_preview || "No OCR text available"}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
